/**
 * 🏹 ARTEMIS - The Lead Hunter
 * ═══════════════════════════════════════════════════════════════
 * 
 * Scan Reddit, Forums & Communities, and Web for high-intent leads
 * actively seeking solutions your product provides.
 * 
 * TIERED ACCESS:
 * - Free: Reddit only (no signup required)
 * - Pro: Reddit + Forums + Web + Lead Nurturing
 * - Enterprise: Reddit + Forums + Web + Deep Scan + Lead Nurturing
 * 
 * LEAD NURTURING (Pro/Enterprise):
 * - Track sent responses
 * - 7-day follow-up reminders
 * - Conversion tracking
 * 
 * "She never misses her mark."
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { RiverButton } from '../ui/RiverFlow';
import { 
  Loader2, Target, Search, ExternalLink, Copy, Check,
  MessageCircle, TrendingUp, Zap, ArrowRight, X,
  Filter, RefreshCw, Crosshair, Flame, Lock, Crown,
  Sparkles, ChevronRight, Globe, Newspaper, Briefcase,
  Bell, Send, CheckCircle, Clock, Archive, PartyPopper,
  Plus, Trash2, ScanSearch, Pencil
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform configurations
const PLATFORMS = {
  reddit: { name: 'Reddit', emoji: '🔴', color: 'from-orange-500 to-red-500', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  forums: { name: 'Forums', emoji: '💬', color: 'from-indigo-500 to-violet-500', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400' },
  web: { name: 'Web', emoji: '🌐', color: 'from-teal-500 to-cyan-500', bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400' },
  quora: { name: 'Quora', emoji: '🟠', color: 'from-red-500 to-orange-500', bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
  hackernews: { name: 'Hacker News', emoji: '🟧', color: 'from-orange-400 to-amber-500', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-300' },
  indiehackers: { name: 'IndieHackers', emoji: '🚀', color: 'from-blue-500 to-indigo-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  producthunt: { name: 'Product Hunt', emoji: '🐱', color: 'from-orange-500 to-red-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  stackexchange: { name: 'Stack Exchange', emoji: '📚', color: 'from-blue-600 to-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  g2: { name: 'G2 Reviews', emoji: '⭐', color: 'from-yellow-500 to-amber-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' },
  capterra: { name: 'Capterra', emoji: '📊', color: 'from-green-500 to-emerald-500', bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
  trustpilot: { name: 'Trustpilot', emoji: '🏆', color: 'from-green-400 to-teal-500', bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400' },
  deep_scan: { name: 'Deep Scan', emoji: '🔍', color: 'from-purple-500 to-pink-500', bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
};

const INTENT_TYPES = {
  recommendation_seeking: { label: 'Seeking Recommendations', color: 'text-green-400', bg: 'bg-green-500/20' },
  problem_expression: { label: 'Expressing Problem', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  competitor_dissatisfaction: { label: 'Competitor Frustration', color: 'text-red-400', bg: 'bg-red-500/20' },
  buying_signals: { label: 'Buying Intent', color: 'text-purple-400', bg: 'bg-purple-500/20' },
};

// Tier badges
const TierBadge = ({ tier }) => {
  const configs = {
    free: { label: 'Free', bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' },
    pro: { label: 'Pro', bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: Sparkles },
    enterprise: { label: 'Enterprise', bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: Crown },
  };
  const config = configs[tier] || configs.free;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text} border ${config.border}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
    </span>
  );
};

// Score indicator component
const ScoreBar = ({ score, label, color = 'cyan' }) => {
  const colorMap = {
    cyan: 'from-cyan-500 to-blue-500',
    green: 'from-green-500 to-emerald-500',
    yellow: 'from-yellow-500 to-orange-500',
    purple: 'from-purple-500 to-pink-500',
  };
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500 w-16">{label}</span>
      <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full bg-gradient-to-r ${colorMap[color]} rounded-full transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-xs text-gray-400 w-8">{score}</span>
    </div>
  );
};

// Opportunity card component
const OpportunityCard = ({ opportunity, onCopyResponse, onMarkSent, huntId, userTier, leadStatus }) => {
  const { startRiver } = useWorkflow();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);
  const [marked, setMarked] = useState(leadStatus === 'sent' || leadStatus === 'follow_up_due');
  
  // Handle google_* platforms
  const platformKey = opportunity.platform.startsWith('google_') ? opportunity.platform : opportunity.platform;
  const platform = PLATFORMS[platformKey] || PLATFORMS.reddit;
  const intent = INTENT_TYPES[opportunity.intent_type] || INTENT_TYPES.recommendation_seeking;
  
  const canNurture = userTier === 'pro' || userTier === 'enterprise';
  
  const handleCopy = () => {
    navigator.clipboard.writeText(opportunity.suggested_response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (onCopyResponse) onCopyResponse(opportunity);
  };
  
  const handleMarkSent = async () => {
    if (!canNurture || marked || !opportunity.suggested_response) return;
    
    setMarking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/artemis/leads/mark-sent`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          opportunity_id: opportunity.id,
          hunt_id: huntId,
          response_sent: opportunity.suggested_response,
          platform_url: opportunity.url
        })
      });
      
      if (response.ok) {
        setMarked(true);
        if (onMarkSent) onMarkSent(opportunity);
      }
    } catch (err) {
      console.error('Failed to mark sent:', err);
    } finally {
      setMarking(false);
    }
  };
  
  const combinedScore = opportunity.combined_score || (opportunity.intent_score + opportunity.relevance_score);
  const isHot = combinedScore >= 120;
  
  return (
    <div className={`bg-gray-800/50 rounded-xl border ${isHot ? 'border-amber-500/50' : 'border-gray-700'} p-4 hover:border-gray-600 transition-all ${marked ? 'opacity-75' : ''}`}>
      {/* Marked indicator */}
      {marked && (
        <div className="flex items-center gap-2 mb-3 text-xs text-blue-400">
          <Send className="w-3 h-3" />
          <span>Response sent • Follow-up reminder in 7 days</span>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Platform badge */}
          <div className={`w-10 h-10 rounded-lg ${platform.bg} ${platform.border} border flex items-center justify-center flex-shrink-0`}>
            <span className="text-xl">{platform.emoji}</span>
          </div>
          
          {/* Title & Meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isHot && <Flame className="w-4 h-4 text-amber-400" />}
              <span className={`text-xs px-2 py-0.5 rounded-full ${intent.bg} ${intent.color}`}>
                {intent.label}
              </span>
            </div>
            <h4 className="font-medium text-white leading-snug line-clamp-2">
              {opportunity.title}
            </h4>
            {opportunity.author && (
              <p className="text-xs text-gray-500 mt-1">@{opportunity.author}</p>
            )}
          </div>
        </div>
        
        {/* Score badge */}
        <div className={`px-3 py-1.5 rounded-lg ${isHot ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-gray-700'}`}>
          <div className={`text-lg font-bold ${isHot ? 'text-amber-400' : 'text-white'}`}>{combinedScore}</div>
          <div className="text-xs text-gray-500">score</div>
        </div>
      </div>
      
      {/* Score bars */}
      <div className="space-y-1.5 mb-3">
        <ScoreBar score={opportunity.intent_score} label="Intent" color="green" />
        <ScoreBar score={opportunity.relevance_score} label="Relevance" color="cyan" />
      </div>
      
      {/* Matched keywords */}
      {opportunity.matched_keywords?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {opportunity.matched_keywords.map((kw, i) => (
            <span key={i} className="text-xs px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full">
              {kw}
            </span>
          ))}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {opportunity.url && (
          <a
            href={opportunity.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${platform.bg} ${platform.text} hover:opacity-80 transition-opacity text-sm`}
            data-testid={`opportunity-view-${opportunity.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View
          </a>
        )}
        
        {opportunity.suggested_response && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors text-sm"
            data-testid={`opportunity-response-${opportunity.id}`}
          >
            <MessageCircle className="w-3.5 h-3.5" />
            {expanded ? 'Hide' : 'See'} Response
          </button>
        )}
      </div>
      
      {/* Expanded response */}
      {expanded && opportunity.suggested_response && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          {/* Outreach Tip */}
          {opportunity.outreach_tip && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-amber-400 font-medium uppercase tracking-wider">Outreach Strategy</span>
              </div>
              <p className="text-sm text-amber-200/80">{opportunity.outreach_tip}</p>
            </div>
          )}
          
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Suggested Response</span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              data-testid={`copy-response-${opportunity.id}`}
            >
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="bg-gray-900/50 rounded-lg p-3 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
            {opportunity.suggested_response}
          </div>
          
          <div className="flex flex-wrap gap-2 mt-3">
            <RiverButton
              label="Craft Outreach"
              icon="✉️"
              onClick={() => startRiver({
                topic: opportunity.title || 'Lead outreach',
                content: opportunity.suggested_response || '',
                leadData: {
                  author: opportunity.author,
                  platform: opportunity.platform,
                  url: opportunity.url,
                  title: opportunity.title,
                },
                contentType: 'email',
                targetTool: 'generate',
                sourceTool: 'artemis',
                sourceGod: 'Artemis',
              })}
              className="!py-1.5 !px-3 !text-xs"
            />
          </div>
          
          {/* Mark as Sent button (Pro/Enterprise only) */}
          {canNurture && !marked && (
            <button
              onClick={handleMarkSent}
              disabled={marking}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors text-sm font-medium"
              data-testid={`mark-sent-${opportunity.id}`}
            >
              {marking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Tracking...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Mark as Sent (Track for Follow-up)
                </>
              )}
            </button>
          )}
          
          {/* Upgrade prompt for free tier */}
          {!canNurture && (
            <div className="mt-3 p-2 rounded-lg bg-gray-700/50 text-center">
              <span className="text-xs text-gray-400">
                <Lock className="w-3 h-3 inline mr-1" />
                Upgrade to Pro to track responses & get follow-up reminders
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Locked feature card
const LockedFeatureCard = ({ icon: Icon, title, description, tier }) => (
  <div className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-4 relative overflow-hidden">
    <div className="absolute inset-0 backdrop-blur-sm bg-gray-900/60 flex items-center justify-center z-10">
      <div className="text-center">
        <Lock className="w-6 h-6 text-gray-500 mx-auto mb-2" />
        <TierBadge tier={tier} />
      </div>
    </div>
    <div className="flex items-start gap-3 opacity-50">
      <div className="w-10 h-10 rounded-lg bg-gray-700/50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-gray-500" />
      </div>
      <div>
        <h4 className="font-medium text-gray-400">{title}</h4>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  </div>
);

// Nurture Queue Component (Pro/Enterprise)
const NurtureQueue = ({ userTier }) => {
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState(null);
  const [stats, setStats] = useState(null);
  const [updating, setUpdating] = useState({});
  
  const canNurture = userTier === 'pro' || userTier === 'enterprise';
  
  useEffect(() => {
    if (canNurture) {
      fetchQueue();
      fetchStats();
    }
  }, [canNurture]);
  
  const fetchQueue = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/artemis/leads/nurture-queue`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setQueue(data);
    } catch (err) {
      console.error('Failed to fetch nurture queue:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/artemis/leads/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };
  
  const updateStatus = async (leadId, newStatus) => {
    setUpdating(prev => ({ ...prev, [leadId]: true }));
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/artemis/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ lead_id: leadId, status: newStatus })
      });
      fetchQueue();
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdating(prev => ({ ...prev, [leadId]: false }));
    }
  };
  
  if (!canNurture) {
    return (
      <div className="p-6 text-center">
        <Lock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Lead Nurturing</h3>
        <p className="text-gray-400 mb-4">Track responses and get follow-up reminders</p>
        <p className="text-sm text-gray-500">Available on Pro and Enterprise plans</p>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }
  
  const statusButtons = [
    { status: 'replied', icon: MessageCircle, label: 'Got Reply', color: 'green' },
    { status: 'converted', icon: PartyPopper, label: 'Converted!', color: 'purple' },
    { status: 'no_response', icon: Clock, label: 'No Response', color: 'gray' },
    { status: 'archived', icon: Archive, label: 'Archive', color: 'gray' },
  ];
  
  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
            <div className="text-2xl font-bold text-white">{stats.total_tracked}</div>
            <div className="text-sm text-gray-400">Total Tracked</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-blue-500/30">
            <div className="text-2xl font-bold text-blue-400">{stats.pipeline?.awaiting_response || 0}</div>
            <div className="text-sm text-gray-400">Awaiting Response</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-green-500/30">
            <div className="text-2xl font-bold text-green-400">{stats.response_rate}%</div>
            <div className="text-sm text-gray-400">Response Rate</div>
          </div>
          <div className="bg-gray-800/50 rounded-xl p-4 border border-purple-500/30">
            <div className="text-2xl font-bold text-purple-400">{stats.conversion_rate}%</div>
            <div className="text-sm text-gray-400">Conversion Rate</div>
          </div>
        </div>
      )}
      
      {/* Due for follow-up */}
      {queue?.due_leads?.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            Follow-up Due ({queue.due_count})
          </h3>
          <div className="space-y-3">
            {queue.due_leads.map((lead) => (
              <div key={lead.lead_id} className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-yellow-400 mb-1">7+ days since response sent</p>
                    <p className="text-gray-300 text-sm line-clamp-2">{lead.response_sent?.substring(0, 150)}...</p>
                    {lead.platform_url && (
                      <a href={lead.platform_url} target="_blank" rel="noopener noreferrer" 
                         className="text-xs text-blue-400 hover:underline mt-1 inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> View Original
                      </a>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {statusButtons.map(({ status, icon: Icon, label, color }) => (
                      <button
                        key={status}
                        onClick={() => updateStatus(lead.lead_id, status)}
                        disabled={updating[lead.lead_id]}
                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                          color === 'green' ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' :
                          color === 'purple' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' :
                          'bg-gray-700 text-gray-400 hover:bg-gray-600'
                        }`}
                        data-testid={`status-${status}-${lead.lead_id}`}
                      >
                        <Icon className="w-3 h-3" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Upcoming follow-ups */}
      {queue?.upcoming_leads?.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Upcoming ({queue.upcoming_count})
          </h3>
          <div className="space-y-2">
            {queue.upcoming_leads.slice(0, 5).map((lead) => (
              <div key={lead.lead_id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <p className="text-gray-300 text-sm line-clamp-1 flex-1">{lead.response_sent?.substring(0, 80)}...</p>
                  <span className="text-xs text-gray-500 ml-2">
                    Follow-up: {new Date(lead.follow_up_due).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Empty state */}
      {queue?.total_tracked === 0 && (
        <div className="text-center py-8">
          <Send className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Leads Tracked Yet</h3>
          <p className="text-gray-400">Start a hunt and mark responses as sent to track them here</p>
        </div>
      )}
    </div>
  );
};


// Editable list field for profile data
const ProfileListEditor = ({ label, items, color, onUpdate, onAdd, onRemove, testIdPrefix }) => {
  const colorMap = {
    emerald: { dot: 'text-emerald-400', border: 'border-emerald-500/20', bg: 'bg-emerald-500/5' },
    cyan: { dot: 'text-cyan-400', border: 'border-cyan-500/20', bg: 'bg-cyan-500/5' },
    amber: { dot: 'text-amber-400', border: 'border-amber-500/20', bg: 'bg-amber-500/5' },
    red: { dot: 'text-red-400', border: 'border-red-500/20', bg: 'bg-red-500/5' },
  };
  const c = colorMap[color] || colorMap.emerald;
  return (
    <div>
      <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">{label}</label>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className={`flex items-center gap-2 ${c.bg} ${c.border} border rounded-lg px-2.5 py-1.5`}>
            <span className={`${c.dot} text-xs`}>*</span>
            <input
              value={item}
              onChange={(e) => onUpdate(i, e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-300 focus:outline-none"
              data-testid={`${testIdPrefix}-${i}`}
            />
            <button onClick={() => onRemove(i)} className="text-gray-600 hover:text-red-400 transition-colors p-0.5">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button
          onClick={onAdd}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors py-1"
          data-testid={`add-${testIdPrefix}-btn`}
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
    </div>
  );
};

// Main Artemis component
export const ArtemisHunter = ({ onClose, userTier = 'free' }) => {
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('hunt'); // 'hunt' or 'nurture'
  const [profile, setProfile] = useState(null); // analyzed profile
  
  const canNurture = userTier === 'pro' || userTier === 'enterprise';
  
  const analyzeWebsite = async () => {
    if (!url) {
      setError('Enter a URL to analyze');
      return;
    }
    setAnalyzing(true);
    setError(null);
    setProfile(null);
    try {
      const response = await fetch(`${API_URL}/api/artemis/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Analysis failed');
      setProfile(data.profile);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const updateProfileField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const updateProfileList = (field, index, value) => {
    setProfile(prev => {
      const list = [...(prev[field] || [])];
      list[index] = value;
      return { ...prev, [field]: list };
    });
  };

  const addProfileListItem = (field) => {
    setProfile(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), '']
    }));
  };

  const removeProfileListItem = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const pollHuntResults = async (huntId, token) => {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const res = await fetch(`${API_URL}/api/artemis/hunt/${huntId}`, { headers });
        const data = await res.json();
        if (data.status === 'complete') {
          return data;
        }
        if (data.status === 'failed') {
          throw new Error(data.error || 'Hunt failed');
        }
      } catch (err) {
        if (err.message === 'Hunt failed' || i === maxAttempts - 1) throw err;
      }
    }
    throw new Error('Hunt timed out');
  };

  const startHunt = async () => {
    if (!url && !description && !profile) {
      setError('Enter a URL or description');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = {
        url: url || null,
        description: description || null,
      };
      if (profile) {
        body.profile = {
          name: profile.name,
          problems_solved: (profile.problems_solved || []).filter(Boolean),
          target_audience: (profile.target_audience || []).filter(Boolean),
          keywords: (profile.keywords || []).filter(Boolean),
          pain_points: (profile.pain_points || []).filter(Boolean),
          competitors: (profile.competitors || []).filter(Boolean),
          unique_value: profile.unique_value,
        };
      }
      
      const response = await fetch(`${API_URL}/api/artemis/hunt`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Hunt failed');
      }
      
      // Backend returns immediately with hunt_id; poll for results
      if (data.status === 'processing' && data.hunt_id) {
        const results = await pollHuntResults(data.hunt_id, token);
        setResults(results);
      } else {
        setResults(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredOpportunities = results?.opportunities?.filter(opp => {
    if (filter === 'all') return true;
    return opp.platform === filter;
  }) || [];
  
  const currentTier = results?.tier?.current || userTier;
  
  // Results view
  if (results) {
    return (
      <div className="min-h-screen bg-gray-900 text-white">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                  <span className="text-xl">🏹</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-bold text-white">Hunt Complete</h1>
                    <TierBadge tier={currentTier} />
                  </div>
                  <p className="text-sm text-gray-400">
                    {results.stats.total_opportunities} opportunities found in {results.duration_seconds}s
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setResults(null)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  data-testid="new-hunt-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  New Hunt
                </button>
                {onClose && (
                  <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg" data-testid="close-artemis-btn">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-6xl mx-auto px-6 py-8">
          {/* Upgrade banner */}
          {results.tier?.upgrade_message && (
            <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                  <span className="text-blue-300">{results.tier.upgrade_message}</span>
                </div>
                <button className="px-4 py-1.5 bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors">
                  Upgrade Now
                </button>
              </div>
            </div>
          )}
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <div className="text-3xl font-bold text-white">{results.stats.total_opportunities}</div>
              <div className="text-sm text-gray-400">Total Leads</div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-green-500/30">
              <div className="text-3xl font-bold text-green-400">{results.stats.high_intent_count}</div>
              <div className="text-sm text-gray-400">High Intent</div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-cyan-500/30">
              <div className="text-3xl font-bold text-cyan-400">{results.stats.high_relevance_count}</div>
              <div className="text-sm text-gray-400">High Relevance</div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-purple-500/30">
              <div className="text-3xl font-bold text-purple-400">{results.stats.queries_used}</div>
              <div className="text-sm text-gray-400">Queries Used</div>
            </div>
          </div>
          
          {/* Deep Scan indicator */}
          {results.stats.deep_scan_enabled && (
            <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-purple-400" />
                <span className="text-purple-300 font-medium">Deep Scan Active</span>
                <span className="text-gray-500 text-sm">- G2, Capterra, Trustpilot, news & job boards</span>
              </div>
            </div>
          )}
          
          {/* Product Profile */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-xl border border-emerald-500/30 p-6 mb-8">
            <h3 className="font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              Target Profile: {results.profile.name}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-gray-500 uppercase mb-2">Problems Solved</div>
                <ul className="space-y-1">
                  {results.profile.problems_solved.map((p, i) => (
                    <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                      <span className="text-emerald-400">•</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase mb-2">Keywords</div>
                <div className="flex flex-wrap gap-2">
                  {results.profile.keywords.map((k, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Filter tabs */}
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            <button
              onClick={() => setFilter('all')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                filter === 'all' 
                  ? 'bg-white text-gray-900 font-medium' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
              data-testid="filter-all"
            >
              All ({results.stats.total_opportunities})
            </button>
            {Object.entries(results.stats.platform_breakdown || {}).map(([platform, count]) => {
              const p = PLATFORMS[platform] || { emoji: '📌', name: platform, color: 'from-gray-500 to-gray-600' };
              return (
                <button
                  key={platform}
                  onClick={() => setFilter(platform)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                    filter === platform 
                      ? `bg-gradient-to-r ${p.color} text-white font-medium` 
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                  data-testid={`filter-${platform}`}
                >
                  <span>{p.emoji}</span>
                  {p.name} ({count})
                </button>
              );
            })}
          </div>
          
          {/* Locked features for non-enterprise */}
          {currentTier !== 'enterprise' && (
            <div className="mb-8">
              <h4 className="text-sm text-gray-500 uppercase tracking-wider mb-3">Unlock More Lead Sources</h4>
              <div className="grid md:grid-cols-3 gap-3">
                {currentTier === 'free' && (
                  <>
                    <LockedFeatureCard 
                      icon={MessageCircle} 
                      title="Forums & Communities" 
                      description="Quora, HN, IndieHackers, ProductHunt"
                      tier="pro"
                    />
                    <LockedFeatureCard 
                      icon={Globe} 
                      title="Web Scanning" 
                      description="Blogs, reviews, comparison articles"
                      tier="pro"
                    />
                  </>
                )}
                <LockedFeatureCard 
                  icon={Search} 
                  title="Deep Scan" 
                  description="G2, Capterra, Trustpilot, news, jobs"
                  tier="enterprise"
                />
              </div>
            </div>
          )}
          
          {/* Opportunities grid */}
          <div className="grid md:grid-cols-2 gap-4">
            {filteredOpportunities.map((opp) => (
              <OpportunityCard 
                key={opp.id} 
                opportunity={opp} 
                huntId={results.hunt_id}
                userTier={currentTier}
              />
            ))}
          </div>
          
          {filteredOpportunities.length === 0 && (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No opportunities found for this filter</p>
            </div>
          )}
          
          {/* Nurture Queue Section (Pro/Enterprise) */}
          {canNurture && (
            <div className="mt-12 pt-8 border-t border-gray-800">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-400" />
                Lead Nurturing
              </h2>
              <NurtureQueue userTier={currentTier} />
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Input form view
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Sticky Header with Close Button */}
      {onClose && (
        <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">🏹</span>
              <span className="font-bold text-white">Artemis</span>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              data-testid="close-artemis-header-btn"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
      )}
      
      <div className="p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-4">
            <Crosshair className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">ARTEMIS - THE LEAD HUNTER</span>
          </div>
          <h1 className="text-4xl font-bold mb-2">
            <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              🏹 The Hunt
            </span>
          </h1>
          <p className="text-gray-400 text-lg">
            Find people actively seeking what you offer
          </p>
        </div>
        
        {/* Tier indicator */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-4 px-4 py-2 bg-gray-800/50 rounded-full">
            <span className="text-gray-500 text-sm">Your access:</span>
            <TierBadge tier={userTier} />
          </div>
        </div>
        
        {/* Tabs for Hunt / Nurture (Pro/Enterprise only) */}
        {canNurture && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('hunt')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'hunt' 
                    ? 'bg-emerald-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-hunt"
              >
                <Crosshair className="w-4 h-4 inline mr-2" />
                New Hunt
              </button>
              <button
                onClick={() => setActiveTab('nurture')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'nurture' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
                data-testid="tab-nurture"
              >
                <Bell className="w-4 h-4 inline mr-2" />
                Nurture Queue
              </button>
            </div>
          </div>
        )}
        
        {/* Nurture Queue Tab */}
        {activeTab === 'nurture' && canNurture && (
          <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-6">
            <NurtureQueue userTier={userTier} />
          </div>
        )}
        
        {/* Hunt Form Tab */}
        {activeTab === 'hunt' && (
        <div className="bg-gray-800/50 rounded-2xl border border-gray-700 p-6 space-y-6">
          {/* URL Input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Your Website/Product URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); if (profile) setProfile(null); }}
                placeholder="https://yourproduct.com"
                className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all"
                data-testid="hunt-url-input"
              />
              <button
                onClick={analyzeWebsite}
                disabled={!url || analyzing}
                className="px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-all flex items-center gap-2 whitespace-nowrap"
                data-testid="analyze-website-btn"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                ) : (
                  <><ScanSearch className="w-4 h-4" /> Analyze</>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">Analyze first for smarter, more targeted hunts</p>
          </div>

          {/* Profile Editor — shown after analysis */}
          {profile && (
            <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 overflow-hidden">
              <div className="px-5 py-3 bg-violet-500/10 border-b border-violet-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ScanSearch className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-300">Website Intelligence: {profile.name}</span>
                </div>
                <span className="text-xs text-gray-500 flex items-center gap-1"><Pencil className="w-3 h-3" /> Editable</span>
              </div>
              <div className="p-5 space-y-4">
                {/* Unique Value */}
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1 block">Value Proposition</label>
                  <input
                    value={profile.unique_value || ''}
                    onChange={(e) => updateProfileField('unique_value', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-900/70 border border-gray-700 rounded-lg text-sm text-white focus:border-violet-500 focus:outline-none"
                    data-testid="profile-unique-value"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* Problems Solved */}
                  <ProfileListEditor
                    label="Problems Solved"
                    items={profile.problems_solved || []}
                    color="emerald"
                    onUpdate={(i, v) => updateProfileList('problems_solved', i, v)}
                    onAdd={() => addProfileListItem('problems_solved')}
                    onRemove={(i) => removeProfileListItem('problems_solved', i)}
                    testIdPrefix="problems"
                  />

                  {/* Target Audience */}
                  <ProfileListEditor
                    label="Target Audience"
                    items={profile.target_audience || []}
                    color="cyan"
                    onUpdate={(i, v) => updateProfileList('target_audience', i, v)}
                    onAdd={() => addProfileListItem('target_audience')}
                    onRemove={(i) => removeProfileListItem('target_audience', i)}
                    testIdPrefix="audience"
                  />

                  {/* Pain Points */}
                  <ProfileListEditor
                    label="Customer Pain Points"
                    items={profile.pain_points || []}
                    color="amber"
                    onUpdate={(i, v) => updateProfileList('pain_points', i, v)}
                    onAdd={() => addProfileListItem('pain_points')}
                    onRemove={(i) => removeProfileListItem('pain_points', i)}
                    testIdPrefix="pains"
                  />

                  {/* Competitors */}
                  <ProfileListEditor
                    label="Competitors"
                    items={profile.competitors || []}
                    color="red"
                    onUpdate={(i, v) => updateProfileList('competitors', i, v)}
                    onAdd={() => addProfileListItem('competitors')}
                    onRemove={(i) => removeProfileListItem('competitors', i)}
                    testIdPrefix="competitors"
                  />
                </div>

                {/* Keywords */}
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider mb-1.5 block">Hunt Keywords</label>
                  <div className="flex flex-wrap gap-2">
                    {(profile.keywords || []).map((kw, i) => (
                      <div key={i} className="group flex items-center gap-1 px-2.5 py-1 bg-violet-500/15 border border-violet-500/30 rounded-full">
                        <input
                          value={kw}
                          onChange={(e) => updateProfileList('keywords', i, e.target.value)}
                          className="bg-transparent text-xs text-violet-300 w-24 focus:outline-none focus:w-32 transition-all"
                          data-testid={`keyword-${i}`}
                        />
                        <button onClick={() => removeProfileListItem('keywords', i)} className="text-gray-600 hover:text-red-400 transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addProfileListItem('keywords')}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-500 hover:text-violet-400 border border-dashed border-gray-700 hover:border-violet-500/40 rounded-full transition-colors"
                      data-testid="add-keyword-btn"
                    >
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* OR divider */}
          {!profile && (
            <>
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-gray-700" />
                <span className="text-gray-500 text-sm">OR</span>
                <div className="flex-1 h-px bg-gray-700" />
              </div>
          
              {/* Description Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Describe Your Product
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="We help SaaS founders automate their content marketing..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none transition-all resize-none"
                  data-testid="hunt-description-input"
                />
              </div>
            </>
          )}
          
          {/* Tier feature preview */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              What you'll scan
            </label>
            <div className="grid grid-cols-3 gap-2">
              {/* Reddit - always available */}
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <span>🔴</span>
                <span className="text-sm text-orange-400">Reddit</span>
                <Check className="w-3 h-3 text-green-400 ml-auto" />
              </div>
              
              {/* Forums - pro+ */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                userTier !== 'free' 
                  ? 'bg-indigo-500/10 border border-indigo-500/30' 
                  : 'bg-gray-800/50 border border-gray-700'
              }`}>
                <span>💬</span>
                <span className={`text-sm ${userTier !== 'free' ? 'text-indigo-400' : 'text-gray-500'}`}>Forums</span>
                {userTier !== 'free' 
                  ? <Check className="w-3 h-3 text-green-400 ml-auto" />
                  : <Lock className="w-3 h-3 text-gray-600 ml-auto" />
                }
              </div>
              
              {/* Web - pro+ */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                userTier !== 'free' 
                  ? 'bg-teal-500/10 border border-teal-500/30' 
                  : 'bg-gray-800/50 border border-gray-700'
              }`}>
                <span>🌐</span>
                <span className={`text-sm ${userTier !== 'free' ? 'text-teal-400' : 'text-gray-500'}`}>Web</span>
                {userTier !== 'free' 
                  ? <Check className="w-3 h-3 text-green-400 ml-auto" />
                  : <Lock className="w-3 h-3 text-gray-600 ml-auto" />
                }
              </div>
            </div>
          </div>
          
          {/* Error */}
          {error && (
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
              {error}
            </div>
          )}
          
          {/* Submit */}
          <button
            onClick={startHunt}
            disabled={loading || (!url && !description)}
            className="w-full py-4 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25"
            data-testid="start-hunt-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Hunting for leads...
              </>
            ) : (
              <>
                <Crosshair className="w-5 h-5" />
                Start The Hunt
              </>
            )}
          </button>
          
          <p className="text-center text-gray-500 text-sm">
            {userTier === 'free' ? 'Free Reddit scan • No signup required' : 'Takes 30-60 seconds to scan all platforms'}
          </p>
        </div>
        )}
        
        {/* Platform info - only show on hunt tab */}
        {activeTab === 'hunt' && (
        <div className="mt-8">
          <div className="text-center text-gray-500 text-sm mb-4">Platforms scanned based on your tier</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4 rounded-xl bg-gray-800/30 border border-gray-700/50">
              <div className="text-2xl mb-2">🔴</div>
              <div className="text-sm font-medium text-white mb-1">Reddit</div>
              <div className="text-xs text-gray-500">All tiers</div>
            </div>
            <div className={`p-4 rounded-xl ${userTier !== 'free' ? 'bg-indigo-500/5 border border-indigo-500/20' : 'bg-gray-800/30 border border-gray-700/50 opacity-50'}`}>
              <div className="text-2xl mb-2">💬</div>
              <div className="text-sm font-medium text-white mb-1">Forums</div>
              <div className="text-xs text-gray-500">Pro & Enterprise</div>
            </div>
            <div className={`p-4 rounded-xl ${userTier !== 'free' ? 'bg-teal-500/5 border border-teal-500/20' : 'bg-gray-800/30 border border-gray-700/50 opacity-50'}`}>
              <div className="text-2xl mb-2">🌐</div>
              <div className="text-sm font-medium text-white mb-1">Web</div>
              <div className="text-xs text-gray-500">Pro & Enterprise</div>
            </div>
            <div className={`p-4 rounded-xl ${userTier === 'enterprise' ? 'bg-purple-500/5 border border-purple-500/20' : 'bg-gray-800/30 border border-gray-700/50 opacity-50'}`}>
              <div className="text-2xl mb-2">🔍</div>
              <div className="text-sm font-medium text-white mb-1">Deep Scan</div>
              <div className="text-xs text-gray-500">Enterprise only</div>
            </div>
          </div>
        </div>
        )}
      </div>
      </div>
    </div>
  );
};

// Widget for landing page (Free tier) - Uses Portal for proper z-index stacking
export const ArtemisWidget = () => {
  const [showArtemis, setShowArtemis] = useState(false);
  
  // Use Portal to render modal at document body level
  const modalContent = showArtemis ? (
    <div className="fixed inset-0 bg-black/95 z-[9999] overflow-y-auto">
      <ArtemisHunter onClose={() => setShowArtemis(false)} userTier="free" />
    </div>
  ) : null;
  
  return (
    <>
      <div className="text-center">
        <button
          onClick={() => setShowArtemis(true)}
          className="group relative px-8 py-4 bg-gradient-to-r from-emerald-600 via-cyan-600 to-emerald-600 hover:from-emerald-500 hover:via-cyan-500 hover:to-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 flex items-center gap-3 mx-auto"
          data-testid="artemis-widget-btn"
        >
          <Crosshair className="w-6 h-6" />
          <span>Hunt For Leads</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>
        <p className="mt-3 text-gray-500 text-sm">
          🏹 Free Reddit scan • Find people looking for your solution
        </p>
      </div>
      {/* Render modal using Portal to escape parent overflow constraints */}
      {modalContent && ReactDOM.createPortal(modalContent, document.body)}
    </>
  );
};

export default ArtemisHunter;
